import { requestContext } from 'client/utils/request-context/request-context';
import { isNode } from 'client/utils/environment';
import { getErrorProps, loggerMethods, loggerLevels } from 'client/utils/logger/logger-utils';
import { getCurrentScope } from 'client/utils/profiler/profiler';

/**
 * Returns request/page/visitor context (e.g., isNode, visitorId, sessionId, edwPageName). In contrast to additional
 * metadata explicitly passed to a logger call, this metadata is meant only for APMs, not to be shown during local development.
 *
 * @param level the level for current logger call
 * @returns {{}}
 */
function getContextMetadata(level) {
  const loggerMetadata = { ...(requestContext.get('loggerMetadata') ?? {}) };

  loggerMetadata.isNode = isNode();
  loggerMetadata.scope = getCurrentScope();
  loggerMetadata.levelNum = loggerLevels[level];

  return loggerMetadata;
}

/**
 * Logger object that provides logging methods.
 *
 * This object is created using the methods from {@link loggerMethods}.
 *
 * @type {Object|null}
 */

export let logger = null;

function buildLogMessage({ addMetadata, level }, args) {
  let message = '';
  let additionalAttributes = {};
  let err;

  args.forEach(arg => {
    if (arg instanceof Error) {
      err = arg;
    } else if (typeof arg === 'object' && arg && !Array.isArray(arg)) {
      additionalAttributes = { ...additionalAttributes, ...arg };
    } else {
      message = message ? `${message} ${arg}` : `${arg}`;
    }
  });

  let data = { ...(addMetadata && getContextMetadata(level)), ...additionalAttributes };
  err = err || data.err;

  if (err) {
    if (!message) {
      message = err.message;
    }
    const { status } = getErrorProps(err);
    const propsToCopyToTopLevel = status ? { status } : {}; // canary query looks for status at the top level
    data = {
      ...propsToCopyToTopLevel,
      err,
      ...data, // add existing data (request metadata) last so it's not overwritten by Error data
    };
  }

  return { message, data };
}

const isLogPossible = (configLevel, level) => configLevel <= level;
/**
 * @param config
 * @param outputLogger logger for using to output
 * @returns {*}
 */
export function initLogger(config, outputLogger) {
  if (logger) return logger;
  const { level: configLevel, addMetadata } = config;
  const configLevelValue = loggerLevels[configLevel];
  // creates log function for the specific log level
  function levelLogger(level) {
    return function log(...args) {
      const levelValue = loggerLevels[level];
      if (!isLogPossible(configLevelValue, levelValue)) {
        return;
      }

      const { message, data } = buildLogMessage({ addMetadata, level }, args);

      // log message
      try {
        outputLogger[level](data, message);
      } catch (e) {
        console.error('Unexpected logger error: ', e); // eslint-disable-line no-console
      }
    };
  }

  // logger object
  logger = loggerMethods.reduce((acc, level) => {
    acc[level] = levelLogger(level);
    return acc;
  }, {});

  if (!isNode()) {
    window.EDM.logger = logger;
    const buffer = window.EDM?.loggerErrorBuffer;
    while (buffer && buffer.length > 0) {
      const item = buffer.shift();
      logger.error(...item);
    }
  }
  return logger;
}
